import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';

// Create the context
export const CarFilterContext = createContext();

// Create a provider component
export const CarFilterProvider = ({ children }) => {
    const [carList, setCarList] = useState([]);
    const [carMakes, setCarMakes] = useState([]);
    const [carModels, setCarModels] = useState([]);
    const [carVariants, setCarVariants] = useState([]);
    const [carColors, setCarColors] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selectedMake, setSelectedMake] = useState('');
    const [selectedModel, setSelectedModel] = useState('');
    const [selectedVariant, setSelectedVariant] = useState('');
    const [selectedColor, setSelectedColor] = useState('');

    // Fetch car details from backend
    useEffect(() => {
        const fetchCars = async () => {
            try {
                const response = await axios.get('https://zoomautos.co.uk/api/cars');
                setCarList(response.data);
            } catch (error) {
                console.error('Error fetching car details:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCars();
    }, []);

    // Fetch distinct car makes (manufacturers)
    useEffect(() => {
        const fetchCarMakes = async () => {
            try {
                const response = await axios.get('https://zoomautos.co.uk/api/cars/manufacturer');
                setCarMakes(response.data);
            } catch (error) {
                console.error('Error fetching car makes:', error);
            }
        };

        fetchCarMakes();
    }, []);

    // Fetch unique colors based on the car data
    useEffect(() => {
        const fetchColors = async () => {
            try {
                const response = await axios.get('https://zoomautos.co.uk/api/cars');
                const cars = response.data;
                setCarList(cars);

                const colors = [...new Set(cars.map(car => car.exteriorColor))].filter(Boolean).sort();
                setCarColors(colors);
            } catch (error) {
                console.error('Error fetching car details:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchColors();
    }, []);

    // Handle make selection
    const handleMakeChange = (e) => {
        const make = e.target.value;
        setSelectedMake(make);
        setSelectedModel(''); // Reset model
        setSelectedVariant(''); // Reset variant
        setCarVariants([]); // Reset variants

        const selectedManufacturer = carList.filter(car => car.manufacturer === make);
        const models = [...new Set(selectedManufacturer.map(car => car.model))];
        const colors = [...new Set(selectedManufacturer.map(car => car.exteriorColor))];
        setCarModels(models);
        setCarColors(colors);
    };

    // Handle model selection
    const handleModelChange = (e) => {
        const model = e.target.value;
        setSelectedModel(model);
        setSelectedVariant(''); // Reset variant

        const selectedModelCars = carList.filter(car => car.manufacturer === selectedMake && car.model === model);
        const variants = [...new Set(selectedModelCars.map(car => car.variant))];
        setCarVariants(variants);
    };

    // Handle variant selection
    const handleVariantChange = (e) => {
        setSelectedVariant(e.target.value);
    };

    return (
        <CarFilterContext.Provider
            value={{
                carMakes,
                carModels,
                carVariants,
                carColors,
                selectedMake,
                selectedModel,
                selectedVariant,
                selectedColor,
                handleMakeChange,
                handleModelChange,
                handleVariantChange,
                setSelectedColor,
                loading,
            }}
        >
            {children}
        </CarFilterContext.Provider>
    );
};
